import { css } from '@emotion/react';
import { isDefAndNotEmpty, NString } from '@mobalytics/shared';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { media } from '../../../ui/global/media-query';
import { AllowedActionsT, BillingInfoT, PlanT } from '../payment.types';
import { getActivatePlanMessage } from '../utils/get-activate-plan.message';
import { PlanDetails } from './plan-details/plan-details.component';
import { StripeExistingFlow } from './payment-flow/stripe-flow/stripe-existing-flow/stripe-existing-flow.component';
import { PaypalFlowExisting } from './payment-flow/paypal-flow/paypal-flow-existing/paypal-flow-existing.component';
import { PaymentMethods } from './payment-methods/payment-methods.component';

interface Props {
  className?: string;
  productAbbreviation: string;

  selectedPlan: PlanT;
  currentPlanId: NString;
  plansWithDiscount: PlanT[] | null;
  plans: PlanT[];

  allowedActions: AllowedActionsT;
  stripeCard: BillingInfoT['stripe'];
  stripeOtherMethods: BillingInfoT['stripePaymentMethod'];
  paypal: BillingInfoT['paypal'];
}

export const PaymentDetails: React.FC<Props> = (props) => {
  const {
    className,
    productAbbreviation,
    allowedActions,
    currentPlanId,
    plansWithDiscount,
    plans,
    selectedPlan,
    stripeCard,
    stripeOtherMethods,
    paypal,
  } = props;
  const { t } = useTranslation();

  const shouldPrepareBillingInfoFirst =
    selectedPlan.isTrial || (allowedActions.renew && !stripeCard?.card && !stripeOtherMethods);
  const buttonLabel = getActivatePlanMessage(t, allowedActions, selectedPlan.planType);

  const renderFlow = () => {
    if (paypal) {
      return (
        <PaypalFlowExisting
          planId={selectedPlan.id}
          productAbbreviation={productAbbreviation}
          coupon={selectedPlan.appliedCoupon}
          allowedActions={allowedActions}
          submitButtonLabel={buttonLabel}
          paypal={paypal}
          className={className}
        />
      );
    }
    if (stripeCard?.card) {
      return (
        <StripeExistingFlow
          title={t('Credit/debit card')}
          description={`**** **** **** ${stripeCard.card.last4}`}
          submitButtonLabel={buttonLabel}
          planId={selectedPlan.id}
          coupon={selectedPlan.appliedCoupon}
          productAbbreviation={productAbbreviation}
          allowedActions={allowedActions}
        />
      );
    }
    if (stripeOtherMethods) {
      return (
        <StripeExistingFlow
          title={t('Payment method')}
          description={stripeOtherMethods.displayName}
          submitButtonLabel={buttonLabel}
          planId={selectedPlan.id}
          coupon={selectedPlan.appliedCoupon}
          productAbbreviation={productAbbreviation}
          allowedActions={allowedActions}
        />
      );
    }
    return (
      <PaymentMethods
        planId={selectedPlan.id}
        productAbbreviation={productAbbreviation}
        coupon={selectedPlan.appliedCoupon}
        allowedActions={allowedActions}
        shouldPrepareBillingInfoFirst={shouldPrepareBillingInfoFirst}
        submitButtonLabel={buttonLabel}
      />
    );
  };

  return (
    <div css={wrapperCss} className={className}>
      <div css={contentCss}>
        <div css={leftColumn}>{renderFlow()}</div>
        {selectedPlan && (
          <PlanDetails
            productAbbreviation={productAbbreviation}
            plan={selectedPlan}
            plans={isDefAndNotEmpty(plansWithDiscount) ? plansWithDiscount : plans}
            currentPlanId={currentPlanId}
          />
        )}
      </div>
    </div>
  );
};

const wrapperCss = css`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  width: 100%;
`;

const contentCss = css`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 80px;
  width: 100%;
  padding-top: 40px;
  padding-bottom: 40px;
  ${media.medium} {
    flex-direction: column-reverse;
    align-items: center;
    gap: 20px;
    padding-top: 24px;
    padding-bottom: 40px;
  }
`;

const leftColumn = css`
  max-width: 320px;
  flex: 1 1 0%;
  ${media.medium} {
    max-width: 400px;
    width: 100%;
  }
`;
